import { videoSessionActions } from 'APP/Store/VideoSession'
import { isAction } from 'redux'
import type { Middleware } from 'redux'

import { signals, storeName } from './constants'
import type { VideoSessionState } from 'APP/Store/VideoSession/types'

interface OpenTokInterface {
  createSession: (apiKey: string, sessionId: string, token: string) => void
  disconnect: () => void
  sendSignal: (type: string, data: Record<string, string | null | undefined>) => void
}

// We don't import this to prevent circular imports.
// Extend this interface as needed with the specific slices we care about here.
interface RootState {
  [storeName]: VideoSessionState
}

export const createMiddleware = (
  openTokInterface: OpenTokInterface
): Middleware<unknown, RootState> => {
  return (_store) => (next) => (action) => {
    if (videoSessionActions.connectToSession.match(action)) {
      const { apiKey, sessionId, token } = action.payload
      if (apiKey && sessionId && token) {
        openTokInterface.createSession(apiKey, sessionId, token)
      }
      return next(action)
    }

    if (!isAction(action)) {
      return next(action)
    }

    switch (action.type) {
      case videoSessionActions.disconnectFromSession.type:
        openTokInterface.disconnect()
        break
      case videoSessionActions.patientAskingAudioPermission.type:
        openTokInterface.sendSignal(signals.ASKING_FOR_PERMISSION, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
          [signals.properties.PERMISSIONS_TYPE]: signals.properties.permissionTypes.AUDIO,
        })
        break
      case videoSessionActions.patientAskingVideoPermission.type:
        openTokInterface.sendSignal(signals.ASKING_FOR_PERMISSION, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
          [signals.properties.PERMISSIONS_TYPE]: signals.properties.permissionTypes.VIDEO,
        })
        break
      case videoSessionActions.patientAcceptedAudioPermission.type:
        openTokInterface.sendSignal(signals.ACCEPTED_PERMISSION, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
          [signals.properties.PERMISSIONS_TYPE]: signals.properties.permissionTypes.AUDIO,
        })
        break
      case videoSessionActions.patientAcceptedVideoPermission.type:
        openTokInterface.sendSignal(signals.ACCEPTED_PERMISSION, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
          [signals.properties.PERMISSIONS_TYPE]: signals.properties.permissionTypes.VIDEO,
        })
        break
      case videoSessionActions.patientDeclinedAudioPermission.type:
        openTokInterface.sendSignal(signals.DECLINED_PERMISSION, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
          [signals.properties.PERMISSIONS_TYPE]: signals.properties.permissionTypes.AUDIO,
        })
        break
      case videoSessionActions.patientDeclinedVideoPermission.type:
        openTokInterface.sendSignal(signals.DECLINED_PERMISSION, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
          [signals.properties.PERMISSIONS_TYPE]: signals.properties.permissionTypes.VIDEO,
        })
        break
      case videoSessionActions.patientEndedCall.type:
        openTokInterface.sendSignal(signals.ENDED_CALL, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
        })
        break
      case videoSessionActions.patientMutedAudio.type:
        openTokInterface.sendSignal(signals.MUTED_AUDIO, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
        })
        break
      case videoSessionActions.patientUnmutedAudio.type:
        openTokInterface.sendSignal(signals.UNMUTED_AUDIO, {
          [signals.properties.SENDER]: signals.properties.senderTypes.PATIENT,
        })
        break
    }
    return next(action)
  }
}
